/**
 * Where a reader is in a 汉字 writing drill, and how a stroke moves them.
 *
 * The drill (#962) takes a character through four stages, and the help falls
 * away one piece at a time. Each stage is passed {@link passesPerStage} times
 * before the next, and a miss in the last stage takes one pass back.
 * This module is the arithmetic of that and nothing else, so it can be settled
 * in Node and the square in `src/site/xiezi-square.ts` can be about the ink.
 *
 * **Both sides of an Exercise run it.** The page moves a reader through the
 * stages as they write, and the `/user/lianxi/` handler moves the kept copy of
 * their place by the same functions, from `src/user/exercise-progress.ts`. It
 * is under `src/handwriting/` rather than `src/site/` for that reason.
 *
 * **A wrong stroke in the first three stages moves nothing.** The scaffolding
 * is there to catch it, the stroke is shown and drawn again, and the pass goes
 * on. The fourth stage has no scaffolding, so its pass is marked whole, and a
 * pass marked wrong takes back the pass before it. See {@link passMissed}.
 */

/**
 * The four stages, in the order the help falls away.
 *
 * `cued` traces over the outline with the next stroke shown and every stroke
 * snapping into place. `traced` keeps the outline and loses the cue. `snapped`
 * writes into an empty square with the strokes still snapping. `free` writes
 * with nothing, and is marked as the 复习 card is marked.
 */
export const stages = ["cued", "traced", "snapped", "free"] as const;

/** One of the four stages. */
export type Stage = (typeof stages)[number];

/**
 * How many times each stage is passed before the next.
 *
 * Hugh's starting value from 2026-10-13, to adjust once readers use the drill.
 */
export const passesPerStage = 5;

/** Where a reader is in the drill. */
export interface DrillProgress {
  /** Which stage, as an index into {@link stages}, or their count once done. */
  readonly stage: number;

  /** How many passes of the stage are done. */
  readonly pass: number;

  /** Which stroke of the character comes next in this pass. */
  readonly stroke: number;
}

/** The beginning of the drill. */
export function drillStart(): DrillProgress {
  return { stage: 0, pass: 0, stroke: 0 };
}

/** The stage the reader is in, or nothing once the drill is finished. */
export function stageOf(progress: DrillProgress): Stage | undefined {
  return stages[progress.stage];
}

/** Whether every stage has been passed. */
export function isFinished(progress: DrillProgress): boolean {
  return progress.stage >= stages.length;
}

/** Whether the outline is shown under the ink in this stage. */
export function showsOutline(stage: Stage): boolean {
  return stage === "cued" || stage === "traced";
}

/**
 * What the square's outline attribute says where the reader is, which the
 * stylesheet draws off and `src/site/xiezi-view.ts` sets. Shown in the first two
 * stages, hidden in the rest and once the drill is finished.
 */
export function outlineState(progress: DrillProgress): "shown" | "hidden" {
  const stage = stageOf(progress);

  return stage !== undefined && showsOutline(stage) ? "shown" : "hidden";
}

/** Whether the stroke wanted next is pointed out in this stage. */
export function cuesNext(stage: Stage): boolean {
  return stage === "cued";
}

/** Whether a stroke the reader draws snaps onto its median in this stage. */
export function snaps(stage: Stage): boolean {
  return stage !== "free";
}

/**
 * Whether the reader's ink stays on the pad where they are, to be marked
 * whole, which is the stage that does not snap.
 */
export function inkStays(progress: DrillProgress): boolean {
  const stage = stageOf(progress);

  return stage !== undefined && !snaps(stage);
}

/**
 * One pass of the stage done.
 *
 * The fifth pass moves the reader to the next stage, and the fifth pass of the
 * last stage finishes the drill.
 */
export function passDone(progress: DrillProgress): DrillProgress {
  const pass = progress.pass + 1;

  return pass >= passesPerStage
    ? { stage: progress.stage + 1, pass: 0, stroke: 0 }
    : { ...progress, pass, stroke: 0 };
}

/**
 * One stroke placed, which finishes the pass once it was the last one.
 *
 * @param progress - Where the reader is.
 * @param strokes - How many strokes the character has.
 * @returns Where they are now.
 */
export function strokePlaced(
  progress: DrillProgress,
  strokes: number,
): DrillProgress {
  const stroke = progress.stroke + 1;

  return stroke >= strokes ? passDone(progress) : { ...progress, stroke };
}

/** The last stroke placed taken back, for an Undo. */
export function strokeTakenBack(progress: DrillProgress): DrillProgress {
  return { ...progress, stroke: Math.max(0, progress.stroke - 1) };
}

/** The pass started again, for a Clear. */
export function passCleared(progress: DrillProgress): DrillProgress {
  return { ...progress, stroke: 0 };
}

/**
 * A pass marked wrong, which takes one pass back.
 *
 * A miss on the third pass of the last stage leaves the reader on its second,
 * and a miss on its first puts them on the last pass of the stage before. A
 * reader who has lost the character from memory therefore ends up in the
 * stage of help before it after a run of misses, and one slip costs them a
 * pass rather than five.
 *
 * Until 2026-10-15 a miss dropped the reader to the first pass of the stage
 * before. Hugh found that too harsh on characters the recogniser confuses with
 * a neighbour, writing 晴 and being read as 腈 every time.
 */
export function passMissed(progress: DrillProgress): DrillProgress {
  if (progress.pass > 0) {
    return { ...progress, pass: progress.pass - 1, stroke: 0 };
  }

  return progress.stage === 0
    ? drillStart()
    : { stage: progress.stage - 1, pass: passesPerStage - 1, stroke: 0 };
}

/**
 * Whether the character is hidden from the reader at this point, so that they
 * write it from what they remember of it.
 *
 * The first two stages trace over the outline, and naming the character beside
 * an outline of it gives nothing away. From the third stage on the square is
 * empty, and the character written anywhere else on the page would be the
 * answer. `src/site/xiezi-view.ts` marks the block, and the stylesheet hides
 * the stroke figure above it and the character in the row over an Exercise.
 */
export function hidesCharacter(progress: DrillProgress): boolean {
  const stage = stageOf(progress);

  return stage !== undefined && !showsOutline(stage);
}
